import { useState, useEffect } from 'react'
import type { WizardStepProps } from '../../../../types/builder.types'
import { supabase } from '../../../../lib/supabase'

interface SaveStepProps extends WizardStepProps {
  editingComponentId?: string | null
  onComplete?: () => void
}

export function SaveStep({ config, onPrev, editingComponentId, onComplete }: SaveStepProps) {
  const [componentName, setComponentName] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedComponentId, setSavedComponentId] = useState<string | null>(null)
  const isEditing = !!editingComponentId

  // Cargar nombre y estado de publicación cuando estamos editando
  useEffect(() => {
    if (editingComponentId) {
      loadComponentInfo(editingComponentId)
    }
  }, [editingComponentId])

  const loadComponentInfo = async (componentId: string) => {
    try {
      // @ts-ignore
      const { data, error } = await supabase
        .from('components')
        .select('name, is_published')
        .eq('id', componentId)
        .single()

      if (error) throw error

      if (data) {
        setComponentName((data as any).name)
        setIsPublished((data as any).is_published)
        setSavedComponentId(componentId)
      }
    } catch (err) {
      console.error('Error cargando info del componente:', err)
    }
  }

  const handleSave = async () => {
    if (!componentName.trim()) {
      setError('Por favor ingresa un nombre para el componente')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditing && editingComponentId) {

        console.log("config", config);
        // UPDATE existing component
        const { error: updateError } = await supabase
          .from('components')
          // @ts-ignore
          .update({
            name: componentName.trim(),
            type: config.componentType,
            data_source: config.dataSource,
            configuration: config as any,
            is_published: isPublished,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingComponentId)

        if (updateError) throw updateError

        setSavedComponentId(editingComponentId)
      } else {
        // INSERT new component
        const { data: { user } } = await supabase.auth.getUser()

        const { data, error: insertError } = await supabase
          .from('components')
          // @ts-ignore
          .insert({
            name: componentName.trim(),
            type: config.componentType,
            data_source: config.dataSource,
            configuration: config as any,
            is_published: isPublished,
            created_by: user?.id || null,
          })
          .select('id')
          .single()

        if (insertError) throw insertError

        // @ts-ignore
        setSavedComponentId(data?.id || null)
      }

      setSaved(true)
    } catch (err) {
      console.error('Error guardando componente:', err)
      setError('Error al guardar el componente. Por favor intenta de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  if (saved) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">✓</span>
          </div>
          <h2 className="text-3xl font-bold text-green-700 mb-2">
            ¡Componente {isEditing ? 'Actualizado' : 'Guardado'}!
          </h2>
          <p className="text-gray-600">
            Tu componente <strong>{componentName}</strong> ha sido {isEditing ? 'actualizado' : 'guardado'} exitosamente.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-3">¿Qué sigue?</h3>
          <div className="text-left space-y-2 text-sm text-blue-800">
            <p>✓ Tu componente está guardado en la base de datos</p>
            <p>✓ ID del componente: <code className="bg-blue-100 px-2 py-1 rounded">{savedComponentId}</code></p>
            {isPublished ? (
              <p>✓ El componente está <strong>publicado</strong> y visible públicamente</p>
            ) : (
              <p>⚠️ El componente NO está publicado (solo visible para ti)</p>
            )}
            <p className="mt-4">
              <strong>Próximo paso:</strong> Agrega este componente a un dashboard para que los usuarios puedan verlo.
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          {onComplete ? (
            <button
              onClick={onComplete}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700"
            >
              ← Volver a la Lista
            </button>
          ) : (
            <>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700"
              >
                Crear Otro Componente
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50"
              >
                Ver Lista de Componentes
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver a Vista Previa
      </button>

      <h2 className="text-2xl font-bold mb-6">
        Paso 5: {isEditing ? 'Actualizar' : 'Guardar'} Componente
      </h2>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-lg mb-4">Resumen del Componente</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Tipo:</span>
            <p className="font-medium capitalize">{config.componentType}</p>
          </div>
          <div>
            <span className="text-gray-600">Fuente de datos:</span>
            <p className="font-medium">{config.dataSource}</p>
          </div>
          {config.dataField && (
            <div>
              <span className="text-gray-600">Campo:</span>
              <p className="font-medium">{config.dataField}</p>
            </div>
          )}
          {config.aggregation && (
            <div>
              <span className="text-gray-600">Agregación:</span>
              <p className="font-medium">{config.aggregation}</p>
            </div>
          )}
          {config.metadata?.title && (
            <div className="col-span-2">
              <span className="text-gray-600">Título:</span>
              <p className="font-medium">{config.metadata.title}</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Form */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="space-y-6">
          {/* Component Name */}
          <div>
            <label className="block font-medium mb-2">
              Nombre del Componente <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={componentName}
              onChange={(e) => setComponentName(e.target.value)}
              placeholder="Ej: Total Volumen Forestal, Distribución por Municipio..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">
              Este nombre te ayudará a identificar el componente en el panel de administración
            </p>
          </div>

          {/* Publish Toggle */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="publish-toggle"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="mt-1 w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <label htmlFor="publish-toggle" className="font-medium cursor-pointer">
                Publicar componente
              </label>
              <p className="text-sm text-gray-600 mt-1">
                {isPublished
                  ? '✓ El componente será visible para todos los usuarios'
                  : '⚠️ El componente solo será visible para administradores'}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Después de guardar, podrás agregar este componente a
              cualquier dashboard desde el panel de administración.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onPrev}
          className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50"
        >
          ← Volver
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !componentName.trim()}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? `${isEditing ? 'Actualizando' : 'Guardando'}...` : `${isEditing ? 'Actualizar' : 'Guardar'} Componente`}
        </button>
      </div>
    </div>
  )
}
